import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger
} from 'components/shared/Tooltip';
import { ReactNode, useRef, useEffect, useState } from 'react';
import { tokens } from 'theme';
import { useIsMobile } from 'lib/hooks/useIsBreakpointWidth/useIsMobile';
type Props = {
  children: ReactNode;
  content?: ReactNode;
  adaptive?: boolean;
  isVisible?: boolean;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  offset?: [number, number];
  className?: string;
  animation?: string;
  maxWidth?: string;
  delay?: number;
  anchorId?: string;
};

const VideoPlayerControlPanel = ({
  children,
  content,
  adaptive = false,
  isVisible = false,
  placement = 'top',
  offset = [0, -(tokens.sizes?.[2] ?? 8)],
  className = '',
  animation = '',
  maxWidth = '',

  ...props
}: Props) => {
  const [internalVisible, setInternalVisible] = useState(isVisible);
  const closeTimeout = useRef<NodeJS.Timeout>(null);
  const { isMobile } = useIsMobile({
    checkDevice: false,
    breakPointType: 'tui'
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (isVisible) {
        setInternalVisible(true);
        return;
      }

      if (isMobile) {
        closeTimeout.current = setTimeout(() => {
          setInternalVisible(false);
        }, 1000);
        return;
      }

      setInternalVisible(false);
    };

    // Clear existing timeout before handling new visibility state
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }

    handleVisibilityChange();

    // Cleanup function
    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, [isVisible, isMobile]);

  return (
    <TooltipRoot open={internalVisible}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={placement}
        sideOffset={Math.abs(offset[1])}
        align="center"
        className={`bg-white p-0 shadow-md transition-opacity ${
          animation === 'scale' ? 'scale-100 data-[state=closed]:scale-95' : ''
        } ${className}`}
        style={{
          transform: adaptive ? 'none' : undefined,
          maxWidth: maxWidth || undefined,
          opacity: internalVisible ? 1 : 0
        }}
        onPointerDownOutside={e => e.preventDefault()}
        {...props}
      >
        <div
          className="touch-manipulation" // Better touch handling
          onClick={e => e.stopPropagation()} // Prevent click propagation
        >
          {content}
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
};

export default VideoPlayerControlPanel;
